import { Suspense, useMemo } from 'react'
import type { ICellRendererParams } from 'ag-grid-community'
import type { AgGridReactProps } from 'ag-grid-react'
import {
  CheckCircleOutline,
  InfoOutlined,
  NotInterested
} from '@mui/icons-material'

import AgGridReactLazy from 'common/components/LazyLoaded/AgGridReact'

import { standardDateFormatter } from 'features/RiskManagement/Components/Tables/utils/formatters'

import getRawValueTooltip from './utils/getRawValueTooltip'

const headerClasses = 'tracking-[.02em] border-b-[1px] border-b-gray-500'

interface IRecentImportsTableRowData {
  id: number
  importedBy: string
  importDate: string
  importStatus: string // REVIEW: obj?
  validationStatus: string
  committedBy: string
  commitDate: string | null
  eventTables: string[]
}

interface IRecentImportsTable {
  rowData: IRecentImportsTableRowData[]
  infoCallback?: (id: number) => void
  statusCallback: (
    id: number,
    type: 'commit' | 'EventTableCommit' | 'reject'
  ) => void
}

const defaultColDef = {
  suppressHeaderMenuButton: true,
  flex: 1,
  tooltipValueGetter: getRawValueTooltip
}

const formatCommitDate = (
  commitDate: NonNullable<IRecentImportsTableRowData['commitDate']>
) =>
  new Date(commitDate).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  })

// virtualization is messing up columns rendering in tests
// Recent Imports Table renders all headers test rendered 4 columns only
const isTestMode = import.meta.env.MODE === 'test'

/**
 * Data Management Imports Page Recent Imports Table
 */
const RecentImportsTable = ({
  rowData,
  infoCallback,
  statusCallback
}: IRecentImportsTable) => {
  const columnDefs: AgGridReactProps['columnDefs'] = useMemo(
    () => [
      {
        field: 'id',
        sortable: true,
        maxWidth: 80,
        sort: 'desc',
        headerClass: headerClasses
      },
      {
        field: 'importedBy',
        sortable: true,
        headerClass: headerClasses
      },
      {
        field: 'eventTables',
        resizable: true,
        filter: 'set',
        headerClass: headerClasses
      },
      {
        field: 'importDate',
        sortable: true,
        headerClass: headerClasses,
        valueFormatter: standardDateFormatter
      },
      {
        field: 'importStatus',
        headerName: 'Status',
        autoHeight: true,
        minWidth: 256,
        headerClass: headerClasses,
        cellClass: (params) =>
          params.data?.importStatus.toLowerCase() === 'validated' ? 'flex' : '',
        cellRenderer: (
          params: ICellRendererParams<IRecentImportsTableRowData>
        ) => {
          const { committedBy, commitDate, importStatus, id } = params.data || {
            committedBy: '',
            commitDate: null,
            importStatus: '',
            id: 0
          }

          // TODO: [FE DEBT] Extract ImportTable Status Badges Into Modular Components
          switch (importStatus.toLowerCase()) {
            case 'committed':
              return (
                <div className='mb-3 mt-2 flex flex-col flex-wrap'>
                  <div className='mb-1 font-medium leading-none text-green'>
                    {importStatus}
                  </div>
                  {commitDate && (
                    <label className='text-sm font-medium text-gray-500'>
                      {committedBy} on {formatCommitDate(commitDate)}
                    </label>
                  )}
                </div>
              )
            case 'rejected':
              return (
                <div className='mb-3 mt-2 flex flex-col flex-wrap'>
                  <div className='mb-1 font-medium leading-none'>
                    {importStatus}
                  </div>
                  {commitDate && (
                    <label className='text-sm font-medium text-gray-500'>
                      {committedBy} on {formatCommitDate(commitDate)}
                    </label>
                  )}
                </div>
              )
            case 'failed':
              return (
                <span className='font-medium text-red'>{importStatus}</span>
              )
            case 'validated':
              return (
                <div className='text-green'>
                  <button
                    className={[
                      'mr-2 py-[0.3rem] pl-2 pr-3',
                      'rounded bg-green',
                      'text-white',
                      'font-medium leading-[18px]',
                      'tracking-[.01em]'
                    ].join(' ')}
                    onClick={() => {
                      statusCallback(id, 'EventTableCommit')
                    }}
                  >
                    <CheckCircleOutline
                      sx={{ fontSize: '1.125rem' }}
                      className='mr-1'
                    />
                    Commit
                  </button>
                  <button
                    className={[
                      'mr-2 py-[0.3rem] pl-2 pr-3',
                      'rounded bg-gray-100',
                      'text-gray-700',
                      'font-medium leading-[18px]',
                      'tracking-[.01em]'
                    ].join(' ')}
                    onClick={() => {
                      statusCallback(id, 'reject')
                    }}
                  >
                    <NotInterested
                      sx={{ fontSize: '1.125rem' }}
                      className='mr-1'
                    />
                    Reject
                  </button>
                </div>
              )

            default:
              return <>{importStatus}</>
          }
        }
      },
      {
        field: 'validationStatus',
        headerName: '',
        width: 64,
        headerClass: headerClasses.concat(' pointer-events-none'),
        cellRenderer: (
          params: ICellRendererParams<IRecentImportsTableRowData>
        ) => {
          const rowId = params.data?.id

          if (rowId == null) {
            return <></>
          }

          return (
            <button className='mt-2 flex' onClick={() => infoCallback?.(rowId)}>
              <InfoOutlined className='text-gray-500' />
            </button>
          )
        }
      }
    ],
    [infoCallback, statusCallback]
  )

  return (
    <div className='flex w-1/3 flex-1 flex-col bg-white pl-10'>
      <div
        className={[
          'mb-2 ml-2 mt-6',
          'font-medium uppercase',
          'tracking-[.03em] text-gray-500'
        ].join(' ')}
      >
        Recent Imports
      </div>
      <div className='ag-theme-lighthouse flex-1'>
        <Suspense fallback='Loading table...'>
          <AgGridReactLazy
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowData={rowData}
            rowClass='border-b-gray-300'
            suppressColumnVirtualisation={isTestMode}
            tooltipInteraction
          />
        </Suspense>
      </div>
    </div>
  )
}

export default RecentImportsTable
